import { useEffect, useState } from "react";

import { getFullName } from "@vega-api/custom/utils/user";
import { getWorkbenchInvestors } from "@vega-api/generated/auth-workbench-v1/auth-workbench-v1";
import {
  SearchUser,
  type SearchUserItem,
} from "@vega-core/components/Form/Controls/Input/SearchUser/SearchUser";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";

export type PickedInvestor = {
  id: string;
  name: string;
  email: string;
};

/**
 * Investor-user autocomplete fed by the existing workbench investor search
 * endpoint — naturally limited to investor portal users, so the generation
 * flow needs no additional eligibility filtering.
 */
export const InvestorUserPicker = ({
  onSelect,
}: {
  onSelect: (user: PickedInvestor | null) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<SearchUserItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    let cancelled = false;

    const search = async () => {
      setIsSearching(true);

      try {
        const response = await getWorkbenchInvestors({
          searchQuery: debouncedSearchTerm,
          page: 1,
          pageSize: 10,
        });

        if (cancelled) return;
        setUsers(
          (response.data ?? []).map((investor) => {
            const fullName = getFullName(investor);

            return {
              id: investor.id,
              searchValue: `${fullName} ${investor.email}`.toLowerCase(),
              content: fullName,
              user: {
                id: investor.id,
                firstName: investor.firstName,
                lastName: investor.lastName,
                email: investor.email,
              },
            };
          }),
        );
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    };

    void search();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchTerm]);

  return (
    <div className="flex flex-col gap-1">
      <span className="body-base-medium">
        {m.settings_reportingApiKeys_generate_user()}
      </span>
      <SearchUser
        data-testid="reporting-api-keys-user-picker"
        isLoading={isSearching}
        name="reporting-api-keys-user-picker"
        noPadding
        onChange={setSearchTerm}
        onUserSelect={(item) =>
          onSelect(
            item
              ? {
                  id: item.user.id,
                  name: getFullName(item.user),
                  email: item.user.email,
                }
              : null,
          )
        }
        placeholder={m.settings_reportingApiKeys_generate_userPlaceholder()}
        users={users}
      />
    </div>
  );
};
